import { useState, useEffect } from 'react';
import { getUserPermissions, getUserPermissionsWithNormalization } from '../services/userService';
import authService from '../services/authService';

export const useRoleAccess = () => {
  const [permissions, setPermissions] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        setLoading(true);
        
        // First try to get role from JWT token
        const currentUser = authService.getCurrentUser();
        if (currentUser && currentUser.token) {
          // Decode JWT token to get role
          const tokenPayload = JSON.parse(atob(currentUser.token.split('.')[1]));
          let roleFromToken = tokenPayload.role;
          
          // If admin role is blank, consider it as ADMIN role
          // This ensures backward compatibility for existing users without explicit roles
          if (!roleFromToken || roleFromToken.trim() === '') {
            roleFromToken = 'ADMIN';
          }
          
          if (roleFromToken) {
            setUserRole(roleFromToken);
            // Set permissions based on role
            const rolePermissions = getPermissionsByRole(roleFromToken);
            setPermissions(rolePermissions);
            setLoading(false);
            return;
          }
        }
        
        // Fallback to API call if no role in token
        const data = await getUserPermissionsWithNormalization();
        
        setPermissions(data.permissions);
        setUserRole(data.userRole);
      } catch (err) {
        console.error('Error fetching user permissions:', err);
        setError(err);
        // Set default permissions for ADMIN role if API fails
        const defaultPermissions = getPermissionsByRole('ADMIN');
        setPermissions(defaultPermissions);
        setUserRole('ADMIN');
      } finally {
        setLoading(false);
      }
    };

    fetchPermissions();
  }, []);

  const getPermissionsByRole = (role) => {
    const permissions = {
      'ADMIN': {
        canManageAdmins: true,
        canManageUsers: true,
        canManageAttendance: true,
        canManageAdvances: true,
        canManageSalarySlips: true,
        canManageSites: true,
        canManageVersions: true,
        canAccessWhiteboard: true,
        canClearMonthData: true,
        canViewAllData: true
      },
      'MANAGER': {
        canManageAdmins: false,
        canManageUsers: true,
        canManageAttendance: true,
        canManageAdvances: true,
        canManageSalarySlips: true,
        canManageSites: true,
        canManageVersions: false,
        canAccessWhiteboard: true,
        canClearMonthData: true,
        canViewAllData: true
      },
      'IT': {
        canManageAdmins: false,
        canManageUsers: false,
        canManageAttendance: false,
        canManageAdvances: false,
        canManageSalarySlips: false,
        canManageSites: false,
        canManageVersions: true,
        canAccessWhiteboard: false,
        canClearMonthData: false,
        canViewAllData: false
      }
    };
    
    return permissions[role] || permissions['MANAGER']; // Default to MANAGER if role not found
  };

  const hasPermission = (permission) => {
    if (!permissions) return false;
    return permissions[permission] === true;
  };

  const canAccessPage = (pageName) => {
    const pagePermissions = {
      'admin-management': 'canManageAdmins',
      'user-management': 'canManageUsers',
      'attendance-management': 'canManageAttendance',
      'advance-management': 'canManageAdvances',
      'salary-slip-management': 'canManageSalarySlips',
      'salary-payment': 'canManageSalarySlips',
      'site-management': 'canManageSites',
      'version-management': 'canManageVersions',
      'whiteboard': 'canAccessWhiteboard'
    };

    const requiredPermission = pagePermissions[pageName];
    if (!requiredPermission) return true; // Default to accessible if no specific permission defined

    return hasPermission(requiredPermission);
  };

  const canPerformAction = (action) => {
    const actionPermissions = {
      'clear-month-data': 'canClearMonthData',
      'delete-users': 'canManageUsers',
      'delete-admins': 'canManageAdmins',
      'manage-versions': 'canManageVersions'
    };

    const requiredPermission = actionPermissions[action];
    if (!requiredPermission) return true; // Default to allowed if no specific permission defined

    return hasPermission(requiredPermission);
  };

  const getRoleDescription = (role) => {
    const roleDescriptions = {
      'ADMIN': 'Full system access - can manage everything',
      'MANAGER': 'Operational access - can manage users, attendance, advances, salary, and sites',
      'IT': 'Version management only - can only access version management features'
    };
    return roleDescriptions[role] || 'Unknown role';
  };

  const getRoleColor = (role) => {
    const roleColors = {
      'ADMIN': 'red',
      'MANAGER': 'blue',
      'IT': 'green'
    };
    return roleColors[role] || 'default';
  };

  return {
    permissions,
    userRole,
    loading,
    error,
    hasPermission,
    canAccessPage,
    canPerformAction,
    getRoleDescription,
    getRoleColor
  };
};
